/**
 * PositionCalculator - カード位置計算ユーティリティ
 * プレイヤーの手札、デッキ、捨て札の位置を計算
 * チーム対応：ローカルプレイヤーのチームを下部に、敵チームを上部に配置
 */
import { PHASER_CONFIG } from '../../utils/constants';

export type PlayerPosition = 'bottom' | 'bottom-left' | 'bottom-right' | 'top-center' | 'top-left' | 'top-right';

export interface CardPosition {
  x: number;
  y: number;
  rotation?: number;
}

/**
 * プレイヤー位置の設定
 * チーム構成に応じて動的に配置を決定
 */
const PLAYER_POSITIONS = {
  // 下部中央（ソロプレイヤー用 or 従来の単独配置）
  'bottom': {
    center: { x: PHASER_CONFIG.WIDTH / 2, y: 850 },
    cardSpacing: 80,
    maxWidth: 900,
    fanAngle: 5,
  },
  // 下部左側（Team 1 左側プレイヤー）
  'bottom-left': {
    center: { x: 500, y: 850 },
    cardSpacing: 60,
    maxWidth: 650,
    fanAngle: 3,
  },
  // 下部右側（Team 1 右側プレイヤー）
  'bottom-right': {
    center: { x: PHASER_CONFIG.WIDTH - 500, y: 850 },
    cardSpacing: 60,
    maxWidth: 650,
    fanAngle: 3,
  },
  // 上部中央（ソロプレイヤー用）
  'top-center': {
    center: { x: PHASER_CONFIG.WIDTH / 2, y: 280 },
    cardSpacing: 50,
    maxWidth: 700,
    fanAngle: 0,
  },
  // 上部左側（従来 or Team 1が上に行く場合）
  'top-left': {
    center: { x: 500, y: 280 },
    cardSpacing: 40,
    maxWidth: 500,
    fanAngle: 0,
  },
  // 上部右側（従来 or Team 1が上に行く場合）
  'top-right': {
    center: { x: PHASER_CONFIG.WIDTH - 500, y: 280 },
    cardSpacing: 40,
    maxWidth: 500,
    fanAngle: 0,
  },
} as const;

export interface TeamLayoutInfo {
  localPlayerTeam: number;
  playerTeams: number[];  // 各プレイヤーのチーム番号 [player0Team, player1Team, player2Team]
}

export class PositionCalculator {
  /**
   * プレイヤーインデックスからポジションを取得（チーム対応版）
   * @param playerIndex プレイヤーインデックス (0, 1, 2)
   * @param totalPlayers プレイヤー総数
   * @param localPlayerIndex ローカルプレイヤーのインデックス
   * @param teamLayout チームレイアウト情報（オプション）
   */
  static getPlayerPosition(
    playerIndex: number,
    _totalPlayers: number,
    localPlayerIndex?: number,
    teamLayout?: TeamLayoutInfo
  ): PlayerPosition {
    // チームレイアウト情報がない場合は従来のロジック
    if (!teamLayout || localPlayerIndex === undefined) {
      return this.getLegacyPosition(playerIndex);
    }

    const { localPlayerTeam, playerTeams } = teamLayout;
    const playerTeam = playerTeams[playerIndex];
    const isLocalPlayer = playerIndex === localPlayerIndex;

    // 3人プレイの場合:
    // Team 1 = Player 0 + Player 1 (デュオ)
    // Team 2 = Player 2 (ソロ)

    if (localPlayerTeam === 1) {
      // ローカルがTeam 1の場合：Team 1を下部に、Team 2を上部に
      if (playerTeam === 1) {
        // Team 1メンバー → 下部横並び
        if (isLocalPlayer) {
          // 自分は左側
          return 'bottom-left';
        } else {
          // チームメイトは右側
          return 'bottom-right';
        }
      } else {
        // Team 2メンバー → 上部中央
        return 'top-center';
      }
    } else {
      // ローカルがTeam 2の場合：Team 2を下部に、Team 1を上部に
      if (playerTeam === 2) {
        // Team 2メンバー（自分）→ 下部中央
        return 'bottom';
      } else {
        // Team 1メンバー → 上部横並び
        // Team 1内での1人目を左、2人目を右に配置
        // (playerIndex===0 で判定すると、ローカルがTeam 2 でplayers[0]の場合に
        // Team 1メンバーが両方elseに落ちて重複するため、Team 1内の相対順で判定)
        const team1Order = playerTeams.slice(0, playerIndex).filter(t => t === 1).length;
        return team1Order === 0 ? 'top-left' : 'top-right';
      }
    }
  }

  /**
   * 従来のポジション計算（後方互換性のため）
   */
  private static getLegacyPosition(playerIndex: number): PlayerPosition {
    switch (playerIndex) {
      case 0:
        return 'bottom';
      case 1:
        return 'top-left';
      case 2:
        return 'top-right';
      default:
        return 'bottom';
    }
  }

  /**
   * 手札のカード位置を計算
   * @param cardCount カード枚数
   * @param position プレイヤー位置
   */
  static calculateHandPositions(
    cardCount: number,
    position: PlayerPosition
  ): CardPosition[] {
    const config = PLAYER_POSITIONS[position];
    const positions: CardPosition[] = [];

    if (cardCount === 0) return positions;

    // カード間隔を計算（最大幅を超えないように調整）
    const totalWidth = (cardCount - 1) * config.cardSpacing;
    const actualSpacing =
      totalWidth > config.maxWidth
        ? config.maxWidth / (cardCount - 1)
        : config.cardSpacing;

    // 開始X位置を計算（中央揃え）
    const startX = config.center.x - ((cardCount - 1) * actualSpacing) / 2;

    for (let i = 0; i < cardCount; i++) {
      const x = startX + i * actualSpacing;
      let y = config.center.y;
      let rotation = 0;

      // 下側のプレイヤーは扇形配置
      if ((position === 'bottom' || position === 'bottom-left' || position === 'bottom-right') && config.fanAngle > 0) {
        // 中央からの距離に基づいて回転と高さを調整
        const normalizedPos = (i - (cardCount - 1) / 2) / Math.max(cardCount - 1, 1);
        rotation = (normalizedPos * config.fanAngle * Math.PI) / 180;
        y += Math.abs(normalizedPos) * 20; // 端のカードを少し下げる
      }

      positions.push({ x, y, rotation });
    }

    return positions;
  }

  /**
   * デッキ位置を取得
   */
  static getDeckPosition(): CardPosition {
    return {
      x: PHASER_CONFIG.DECK_POSITION.x,
      y: PHASER_CONFIG.DECK_POSITION.y,
    };
  }

  /**
   * 捨て札位置を取得
   */
  static getDiscardPosition(): CardPosition {
    return {
      x: PHASER_CONFIG.DISCARD_POSITION.x,
      y: PHASER_CONFIG.DISCARD_POSITION.y,
    };
  }

  /**
   * カードプレイアニメーションの中間位置を計算
   * @param from 開始位置
   * @param to 終了位置
   * @param progress 進行度 (0-1)
   */
  static interpolatePosition(
    from: CardPosition,
    to: CardPosition,
    progress: number
  ): CardPosition {
    const eased = this.easeOutQuad(progress);
    return {
      x: from.x + (to.x - from.x) * eased,
      y: from.y + (to.y - from.y) * eased,
      rotation: (from.rotation || 0) + ((to.rotation || 0) - (from.rotation || 0)) * eased,
    };
  }

  /**
   * イージング関数 (Quad Out)
   */
  private static easeOutQuad(t: number): number {
    return t * (2 - t);
  }

  /**
   * 画面中央位置を取得
   */
  static getCenterPosition(): CardPosition {
    return {
      x: PHASER_CONFIG.WIDTH / 2,
      y: PHASER_CONFIG.HEIGHT / 2,
    };
  }

  /**
   * ランダムな散らばり位置を取得（勝利エフェクト用）
   */
  static getRandomScatterPositions(count: number): CardPosition[] {
    const positions: CardPosition[] = [];
    const centerX = PHASER_CONFIG.WIDTH / 2;
    const centerY = PHASER_CONFIG.HEIGHT / 2;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const distance = 200 + Math.random() * 300;
      positions.push({
        x: centerX + Math.cos(angle) * distance,
        y: centerY + Math.sin(angle) * distance,
        rotation: Math.random() * Math.PI * 2,
      });
    }

    return positions;
  }

  /**
   * チームオーバーレイの配置情報を取得
   * @param localPlayerTeam ローカルプレイヤーのチーム番号
   */
  static getTeamOverlayPosition(localPlayerTeam: number): {
    team1: { y: number; isBottom: boolean };
    team2: { y: number; isBottom: boolean };
  } {
    if (localPlayerTeam === 1) {
      // Team 1が下部、Team 2が上部
      return {
        team1: { y: 850, isBottom: true },
        team2: { y: 280, isBottom: false },
      };
    } else {
      // Team 2が下部、Team 1が上部
      return {
        team1: { y: 280, isBottom: false },
        team2: { y: 850, isBottom: true },
      };
    }
  }
}
